<template>
  <div class="modal fade show" id="productModalOverlay" tabindex="-1" style="display: block;" aria-modal="true" role="dialog">
    <!-- JSON-LD Product Schema per SEO -->
    <script type="application/ld+json" v-if="productSchema">
      {{ JSON.stringify(productSchema) }}
    </script>
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body">
          <div class="product-modal-container">
            <!-- Pulsante di chiusura universale -->
            <button type="button" class="btn-close-custom" @click="closeModal" aria-label="Close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>

            <!-- Sezione Sinistra - Immagine Prodotto -->
            <div class="product-left">
              <div class="carousel-wrapper">
                <div 
                  id="productCarouselH" 
                  class="carousel-custom w-100"
                  @touchstart="handleTouchStart"
                  @touchend="handleTouchEnd"
                >
                  <div class="carousel-inner">
                    <div 
                      v-for="(image, index) in variantImages" 
                      :key="image.imageName || index"
                      class="carousel-item"
                      :class="{ active: index === currentImageIndex }"
                    >
                      <ProductImageZoom :src="image.url" :alt="image.imageName" />
                    </div>
                  </div>
                  <button v-if="variantImages.length > 1" class="carousel-control-prev-custom" type="button" @click="prevImage">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                  </button>
                  <button v-if="variantImages.length > 1" class="carousel-control-next-custom" type="button" @click="nextImage">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                  </button>
                </div>
                
                <!-- Dots Centrati -->
                <div v-if="variantImages.length > 1" class="carousel-indicators-custom">
                  <button 
                    v-for="(_, index) in variantImages"
                    :key="index"
                    type="button" 
                    :class="{ active: index === currentImageIndex }" 
                    @click="setImageIndex(index)"
                  ></button>
                </div>

                <!-- Badge ai lati dei dots (su mobile) -->
                <div class="product-badges-new">
                  <div class="badge-item-new black">{{ t('product.tags.new_drop') }}</div>
                  <div class="badge-item-new outline">{{ t('product.tags.limited_edition') }}</div>
                </div>
              </div>
            </div>

            <!-- Sezione Destra - Dettagli Prodotto -->
            <div class="product-right">
                <div class="product-header">
                  <div class="brand-name">{{ t('brand.name') }}</div>
                  <div v-if="product.categoryTypeSku" class="sku-row-modal">
                    <span class="sku-label-text">COD.</span>
                    <span class="sku-value">{{ product.categoryTypeSku }}</span>
                  </div>
                  <h2 class="product-title">{{ product.name.split(' ')[0] }} {{ productNamePart }}</h2>
                  <div class="product-edition">{{ t('product.tags.blessed_edition') }}</div>
                </div>

              <div class="product-content">
                <div class="price-section">
                  <div class="price-container">
                    <div v-if="product.oldPrice" class="old-price">€{{ product.oldPrice.toFixed(2).replace('.', ',') }}</div>
                    <div class="current-price">€{{ product.price.toFixed(2).replace('.', ',') }}</div>
                    <div v-if="product.discount" class="discount-badge">-{{ product.discount }}%</div>
                    <div class="price-vat-label">{{ t('product.labels.vat_included') }}</div>
                  </div>
                </div>

                <div class="info-icons-section">
                  <div class="info-icon-item">
                    <div class="info-icon-wrapper">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"></path><path d="M2 17l10 5 10-5"></path><path d="M2 12l10 5 10-5"></path></svg>
                    </div>
                    <div class="info-icon-text">
                      <span class="label">{{ t('product.specs.made_in_italy') }}</span>
                    </div>
                  </div>
                  <div class="info-divider"></div>
                  <div class="info-icon-item">
                    <div class="info-icon-wrapper">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path><path d="M12 18V6"></path><path d="M6 12h12"></path></svg>
                    </div>
                    <div class="info-icon-text">
                      <span class="label">{{ t('product.labels.material') }}</span>
                      <span class="sub-label">{{ product.materialComposition || '100% COTONE' }}</span>
                    </div>
                  </div>
                  <div class="info-divider"></div>
                  <div class="info-icon-item">
                    <div class="info-icon-wrapper">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                    </div>
                    <div class="info-icon-text">
                      <span class="label">{{ t('product.specs.fit') }}</span>
                      <span class="sub-label">{{ product.fitSizing?.toUpperCase() || 'REGULAR FIT' }}</span>
                    </div>
                  </div>
                </div>

                <div class="product-description-container mt-4 mb-4">
                  <p class="description-text mb-2" v-html="splitDescription.main"></p>
                  
                  <div v-if="detailEntries.length || modelFitText" class="description-extra-wrapper">
                    <button @click="toggleDetails" class="btn-show-details" type="button" :aria-expanded="showFullDetails">
                      <span class="btn-show-details-label">{{ t('product.labels.show_more') }}</span>
                      <svg :class="{ 'rotated': showFullDetails }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </button>
                    
                    <transition name="expand">
                      <div v-if="showFullDetails" class="extra-content mt-2">
                        <ul class="detail-list">
                          <li v-for="(item, index) in detailEntries" :key="`${item}-${index}`">{{ item }}</li>
                        </ul>
                      </div>
                    </transition>
                  </div>

                  <div v-if="careEntries.length" class="care-instructions mt-4">
                    <button type="button" class="care-toggle" @click="toggleCare" :aria-expanded="showCareDetails">
                      <span class="care-label">{{ t('product.labels.care_instructions') || 'Care Instructions' }}</span>
                      <svg class="care-chevron" :class="{ rotated: showCareDetails }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                    <transition name="expand">
                      <ul v-if="showCareDetails" class="care-list">
                        <li v-for="(item, index) in careEntries" :key="`${item}-${index}`">{{ item }}</li>
                      </ul>
                    </transition>
                  </div>
                </div>

                <div class="options-section">
                  <div class="option-group">
                    <div class="d-flex justify-content-between align-items-baseline mb-2">
                      <label class="option-label m-0">{{ t('product.labels.size') }}</label>
                      <a href="#" class="size-guide-link" @click.prevent="fetchSizeGuide">{{ t('product.size_guide.title') }} <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg></a>
                    </div>
                    <div class="size-guide-meta" v-if="modelFitText">
                      <span>{{ modelFitText }}</span>
                    </div>
                    <div class="size-options-grid">
                      <button
                        v-for="size in sizes"
                        :key="size"
                        class="size-box-btn"
                        :class="{ active: selectedSize === size, 'size-out-of-stock': !isSizeAvailable(size) }"
                        :disabled="!isSizeAvailable(size)"
                        @click="selectSize(size)"
                      >
                        {{ size }}
                      </button>
                    </div>
                  </div>

                  <div class="selection-row">
                    <div class="selection-labels-row">
                      <div class="option-label compact-label">{{ t('product.labels.color') }}</div>
                      <div class="option-label compact-label text-end">{{ t('product.labels.quantity') }}</div>
                    </div>

                    <div class="selection-controls-row">
                      <div class="option-group compact-group">
                        <div class="color-options-grid">
                          <button
                            v-for="color in getAvailableColors"
                            :key="color.colorName"
                            class="color-dot-btn"
                            :class="{ active: selectedColor === color.colorName }"
                            @click="selectColor(color.colorName)"
                            :title="color.colorName"
                          >
                            <span class="color-dot" :style="{ backgroundColor: color.colorHexCode || '#000' }"></span>
                          </button>
                        </div>
                      </div>

                      <div class="option-group compact-group quantity-group">
                        <div class="quantity-selector">
                          <button class="qty-btn" @click="decreaseQty" :disabled="quantity <= 1">−</button>
                          <span class="qty-value">{{ quantity }}</span>
                          <button class="qty-btn" @click="increaseQty">+</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="trust-features-product">
                  <div class="trust-feature-item-product">
                    <div class="feature-icon-product">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                    </div>
                    <div class="feature-text-product">
                      <span class="main-text-product">{{ t('header.cart.trust.secure_payment') }}</span>
                    </div>
                  </div>

                  <div class="trust-feature-item-product">
                    <div class="feature-icon-product">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="1" y="3" width="15" height="13"></rect>
                        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                        <circle cx="5.5" cy="18.5" r="2.5"></circle>
                        <circle cx="18.5" cy="18.5" r="2.5"></circle>
                      </svg>
                    </div>
                    <div class="feature-text-product">
                      <span class="main-text-product">{{ t('header.cart.trust.standard_shipping') }}</span>
                      <span class="sub-text-product">{{ currentLanguage === 'it' ? 'in 24/48h' : 'in 24/48h' }}</span>
                    </div>
                  </div>

                  <div class="trust-feature-item-product">
                    <div class="feature-icon-product">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                      </svg>
                    </div>
                    <div class="feature-text-product">
                      <span class="main-text-product">{{ t('header.cart.trust.returns') }}</span>
                      <span class="sub-text-product">{{ currentLanguage === 'it' ? 'in 14 giorni' : 'in 14 days' }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="product-actions">
                <button class="add-to-cart-btn" @click="addToCart" :disabled="!isAnyVariantAvailable">
                  <span>{{ t('product.add_to_cart') }}</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path><path d="M3 6h18"></path><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
                </button>
              </div>
            </div>

            <!-- Overlay Guida alle Taglie -->
            <transition name="fade">
              <div v-if="showSizeGuide" class="size-guide-wrapper">
                <div class="size-guide-backdrop" @click="showSizeGuide = false"></div>
                <div class="size-guide-overlay">
                  <div class="size-guide-header">
                    <div>
                      <div class="brand-name">{{ t('brand.name') }}</div>
                      <h3 class="size-guide-title">{{ t('product.size_guide.title') }}</h3>
                    </div>
                    <button type="button" class="btn-close-custom" @click="showSizeGuide = false">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                  </div>
                  <div class="size-guide-body">
                    <div v-if="modelFitText" class="size-guide-model-fit">
                      {{ modelFitText }}
                    </div>
                    <div v-if="loadingSizeGuide" class="size-guide-loader">
                      <div class="spinner-luxury"></div>
                    </div>
                    <div v-else class="table-responsive">
                      <table class="size-guide-table">
                        <thead>
                          <tr>
                            <th>{{ $t('product.size_guide.table.size') }}</th>
                            <th>{{ $t('product.size_guide.table.width') }}</th>
                            <th>{{ $t('product.size_guide.table.height') }}</th>
                            <th>{{ $t('product.size_guide.table.sleeve') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="item in sizeGuideData" :key="item.size">
                            <td>{{ item.size }}</td>
                            <td>{{ item.width }}</td>
                            <td>{{ item.height }}</td>
                            <td>{{ item.sleeveLengthCm }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show"></div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import ProductImageZoom from './ProductImageZoom.vue'
import { useProductImages } from '@/composables/useProductImages'
import { useCart } from '@/composables/useCart'
import { useLanguage } from '@/composables/useLanguage'
import { useToast } from '@/composables/useToast'
import { sizeGuideService } from '@/services/sizeGuideService'
import { cookieService } from '@/services/cookieService'

const { currentLanguage, t } = useLanguage()
const toast = useToast()
const { getImageUrl, getSortedVariantImages } = useProductImages()
const { addToCart: addToCartState } = useCart()

const props = defineProps({
  product: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close'])

const selectedColor = ref('')
const selectedSize = ref('L')
const quantity = ref(1)
const showFullDetails = ref(false)
const showCareDetails = ref(false)
const currentYear = ref(new Date().getFullYear())

const currentImageIndex = ref(0)
const touchStartX = ref(0)
const touchEndX = ref(0)

// JSON-LD Product Schema per SEO
const productSchema = computed(() => {
  const p = props.product
  if (!p) return null
  
  const images = []
  if (p.images && Array.isArray(p.images)) {
    p.images.forEach(img => {
      const url = img.imageUrl || img.imageName || img.url
      if (url) {
        const fullUrl = url.startsWith('http') ? url : `https://billyb.it${getImageUrl(url)}`
        images.push(fullUrl)
      }
    })
  }
  if (p.variants && Array.isArray(p.variants)) {
    p.variants.forEach(v => {
      if (v.images && Array.isArray(v.images)) {
        v.images.forEach(img => {
          const url = img.imageUrl || img.imageName || img.url
          if (url) {
            const fullUrl = url.startsWith('http') ? url : `https://billyb.it${getImageUrl(url)}`
            if (!images.includes(fullUrl)) images.push(fullUrl)
          }
        })
      }
    })
  }
  
  const offers = []
  if (p.variants && Array.isArray(p.variants)) {
    p.variants.forEach(v => {
      if (v.size && v.colorName) {
        offers.push({
          "@type": "Offer",
          "url": `https://billyb.it/t-shirt#${p.id}-${v.colorId || v.colorName}-${v.size}`,
          "priceCurrency": "EUR",
          "price": (p.price || 0).toFixed(2),
          "availability": (v.stockQuantity || 0) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "itemCondition": "https://schema.org/NewCondition",
          "sku": `${p.categoryTypeSku || p.id}-${v.colorId || v.colorName}-${v.size}`
        })
      }
    })
  } else {
    offers.push({
      "@type": "Offer",
      "url": `https://billyb.it/t-shirt#${p.id}`,
      "priceCurrency": "EUR",
      "price": (p.price || 0).toFixed(2),
      "availability": p.inStock !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "itemCondition": "https://schema.org/NewCondition",
      "sku": p.categoryTypeSku || String(p.id)
    })
  }
  
  // Se c'è sconto, aggiungi priceSpecification
  const finalPrice = p.discount > 0 && p.oldPrice ? p.price : p.price
  
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": p.name,
    "description": (p.descriptionIt || p.description || '').replace(/<[^>]*>/g, '').substring(0, 500),
    "brand": {
      "@type": "Brand",
      "name": "Billy B"
    },
    "sku": p.categoryTypeSku || String(p.id),
    "gtin": p.gtin || undefined,
    "mpn": p.categoryTypeSku || String(p.id),
    "image": images.length > 0 ? images : [`https://billyb.it/images/products/luminous-blessed-black-front.webp`],
    "offers": offers,
    "productID": String(p.id),
    "category": p.categoryTypeSku ? `TSHIRT` : `Apparel`,
    "material": p.materialComposition || "100% Cotton",
    "color": p.colors?.[0]?.colorName || "Nero",
    "size": p.sizes || ["S", "M", "L", "XL"],
    "manufacturer": {
      "@type": "Organization",
      "name": "Billy B S.r.l.s.",
      "url": "https://billyb.it"
    },
    "audience": {
      "@type": "PeopleAudience",
      "suggestedGender": "Unisex"
    }
  }
})

const nextImage = () => {
  if (variantImages.value.length > 1) {
    currentImageIndex.value = (currentImageIndex.value + 1) % variantImages.value.length
  }
}

const prevImage = () => {
  if (variantImages.value.length > 1) {
    currentImageIndex.value = (currentImageIndex.value - 1 + variantImages.value.length) % variantImages.value.length
  }
}

const setImageIndex = (index) => {
  currentImageIndex.value = index
}

const handleTouchStart = (e) => {
  touchStartX.value = e.changedTouches[0].screenX
}

const handleTouchEnd = (e) => {
  touchEndX.value = e.changedTouches[0].screenX
  handleSwipe()
}

const handleSwipe = () => {
  const swipeThreshold = 50
  if (touchStartX.value - touchEndX.value > swipeThreshold) {
    // Swipe left -> Next image
    nextImage()
  } else if (touchEndX.value - touchStartX.value > swipeThreshold) {
    // Swipe right -> Previous image
    prevImage()
  }
}

const showSizeGuide = ref(false)
const sizeGuideData = ref([])
const loadingSizeGuide = ref(false)

const isNewAPIStructure = computed(() => {
  return props.product.variants && Array.isArray(props.product.variants)
})

const getAvailableColors = computed(() => {
  if (isNewAPIStructure.value && props.product.variants) {
    const colorMap = new Map()
    const colorDisplayOrder = new Map()
    
    props.product.variants.forEach(variant => {
      if (variant.colorName && !colorMap.has(variant.colorName)) {
        colorMap.set(variant.colorName, {
          colorName: variant.colorName,
          colorHexCode: variant.colorHexCode,
          colorId: variant.colorId
        })
      }
      
      if (variant.colorName && variant.images && variant.images.length > 0) {
        const minDisplayOrder = Math.min(...variant.images.map(img => img.displayOrder || Infinity))
        if (!colorDisplayOrder.has(variant.colorName) || minDisplayOrder < colorDisplayOrder.get(variant.colorName)) {
          colorDisplayOrder.set(variant.colorName, minDisplayOrder)
        }
      }
    })
    
    return Array.from(colorMap.values()).sort((a, b) => {
      const orderA = colorDisplayOrder.get(a.colorName) ?? Infinity
      const orderB = colorDisplayOrder.get(b.colorName) ?? Infinity
      return orderA - orderB
    })
  }
  
  return (props.product.colors || []).map(c => ({
    colorName: c.name,
    colorHexCode: c.hexCode,
    colorId: c.name
  }))
})

const colors = computed(() => {
  return getAvailableColors.value.map(c => c.colorName || c.name)
})

const sizes = ['S', 'M', 'L', 'XL']

const getVariantStock = (size, color) => {
  if (!isNewAPIStructure.value || !props.product.variants) return Infinity
  const variant = props.product.variants.find(v => v.colorName === color && v.size === size)
  return variant ? (variant.stockQuantity || 0) : 0
}

const isSizeAvailable = (size) => getVariantStock(size, selectedColor.value) > 0

const isAnyVariantAvailable = computed(() => {
  if (!isNewAPIStructure.value || !props.product.variants) return true
  return props.product.variants.some(v => (v.stockQuantity || 0) > 0)
})

const pickBestSize = () => {
  if (!isNewAPIStructure.value || !props.product.variants) return
  const candidates = sizes.map(size => {
    const stock = getVariantStock(size, selectedColor.value)
    return { size, stock }
  })
  const available = candidates.filter(c => c.stock > 0)
  if (available.length > 0) {
    const lAvailable = available.find(c => c.size === 'L')
    if (lAvailable) {
      selectedSize.value = 'L'
    } else {
      available.sort((a, b) => b.stock - a.stock)
      selectedSize.value = available[0].size
    }
  } else {
    selectedSize.value = sizes[0]
  }
}

const getSelectedVariant = () => {
  if (!isNewAPIStructure.value) return null
  return props.product.variants.find(v => v.colorName === selectedColor.value)
}

const variantImages = computed(() => {
  if (isNewAPIStructure.value) {
    const variant = getSelectedVariant()
    if (variant && variant.images && Array.isArray(variant.images)) {
      return getSortedVariantImages(variant.images)
    }
    return []
  }
  
  if (!selectedColor.value) return []
  const selectedColorObj = props.product.colors?.find(c => c.name === selectedColor.value)
  if (selectedColorObj?.frontImageFileName || selectedColorObj?.backImageFileName) {
    const images = []
    if (selectedColorObj.frontImageFileName) {
      const url = getImageUrl(selectedColorObj.frontImageFileName)
      if (url) images.push({ url, imageName: selectedColorObj.frontImageFileName, displayOrder: 1, isPrimary: true })
    }
    if (selectedColorObj.backImageFileName) {
      const url = getImageUrl(selectedColorObj.backImageFileName)
      if (url) images.push({ url, imageName: selectedColorObj.backImageFileName, displayOrder: 2, isPrimary: false })
    }
    return images
  }
  return []
})

const productNamePart = computed(() => {
  return props.product.name.split(' ').slice(1).join(' ')
})

const localizedDescription = computed(() => {
  return currentLanguage.value === 'it' 
    ? props.product.descriptionIt || props.product.description 
    : props.product.descriptionEn || props.product.description
})

const splitDescription = computed(() => {
  const fullText = localizedDescription.value || ''
  const lines = fullText.split('\n')
  
  const mainText = []
  const bulletPoints = []
  
  lines.forEach(line => {
    const trimmed = line.trim()
    if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.toLowerCase().includes('stampa:') || trimmed.toLowerCase().includes('materiale:')) {
      bulletPoints.push(trimmed)
    } else if (trimmed) {
      mainText.push(trimmed)
    }
  })
  
  return {
    main: mainText.join('<br>'),
    extra: bulletPoints.join('<br>')
  }
})

const localizedCareInstructions = computed(() => {
  return currentLanguage.value === 'it' 
    ? props.product.careInstructionsIt 
    : props.product.careInstructionsEn
})

const modelFitText = computed(() => {
  return currentLanguage.value === 'it'
    ? 'Il modello è alto 188 cm e indossa una taglia L'
    : 'Model is 188 cm / 6\'2" and is wearing size L'
})

const detailEntries = computed(() => {
  const raw = splitDescription.value.extra || ''

  return raw
    .split('<br>')
    .map(item => item.replace(/<[^>]+>/g, '').replace(/^[-•\s]+/, '').trim())
    .filter(Boolean)
})

const careEntries = computed(() => {
  const raw = localizedCareInstructions.value || ''

  return raw
    .split(/\r?\n|\u2022|;/)
    .map(item => item.replace(/^[-•\s]+/, '').trim())
    .filter(Boolean)
})

const toggleDetails = () => {
  showFullDetails.value = !showFullDetails.value
  if (showFullDetails.value) showCareDetails.value = false
}

const toggleCare = () => {
  showCareDetails.value = !showCareDetails.value
  if (showCareDetails.value) showFullDetails.value = false
}

const selectColor = (color) => {
  selectedColor.value = color
  currentImageIndex.value = 0
  pickBestSize()
}

const selectSize = (size) => {
  selectedSize.value = size
}

const increaseQty = () => {
  quantity.value++
}

const decreaseQty = () => {
  if (quantity.value > 1) {
    quantity.value--
  }
}

const closeModal = () => {
  emit('close')
}

const addToCart = async () => {
  try {
    if (!isSizeAvailable(selectedSize.value)) {
      toast.error(t('toast.error.generic'))
      return
    }
    const selectedVariantImage = variantImages.value[3]?.imageName || 
                               variantImages.value[0]?.imageName || 
                               props.product.colors?.find(c => c.name === selectedColor.value)?.frontImageFileName
                               
    addToCartState(props.product, {
      color: selectedColor.value,
      size: selectedSize.value,
      quantity: quantity.value,
      image: selectedVariantImage
    })
  } catch (error) {
    toast.error(t('toast.error.generic'))
  }
}

const fetchSizeGuide = async () => {
  try {
    loadingSizeGuide.value = true
    const data = await sizeGuideService.getSizeGuide(props.product.categoryTypeId)
    sizeGuideData.value = data
    showSizeGuide.value = true
  } catch (error) {
    console.error('[ProductModal] Errore caricamento guida taglie:', error)
    toast.error(t('toast.error.generic'))
    showSizeGuide.value = false
  } finally {
    loadingSizeGuide.value = false
  }
}

onMounted(() => {
  if (!selectedColor.value && getAvailableColors.value.length > 0) {
    selectedColor.value = getAvailableColors.value[0].colorName
    pickBestSize()
  }
  document.documentElement.classList.add('modal-open')
  document.body.classList.add('modal-open')
  window.addEventListener('keydown', closeWithEscape)

  // Meta Pixel: ViewContent
  cookieService.trackPixelEvent('ViewContent', {
    content_ids: [String(props.product.categoryTypeSku || props.product.id)],
    content_type: 'product',
    value: props.product.price,
    currency: 'EUR',
    content_name: props.product.name
  })

  // TikTok Pixel: ViewContent
  cookieService.trackTikTokEvent('ViewContent', {
    content_id: String(props.product.categoryTypeSku || props.product.id),
    contents: [{
      content_id: String(props.product.categoryTypeSku || props.product.id),
      content_type: 'product',
      content_name: props.product.name,
      price: props.product.price
    }],
    content_type: 'product',
    value: props.product.price,
    currency: 'EUR'
  })

  // GA4: view_item
  cookieService.trackGA4EcommerceEvent('view_item', {
    currency: 'EUR',
    value: props.product.price,
    items: [{
      item_id: String(props.product.categoryTypeSku || props.product.id),
      item_name: props.product.name,
      price: props.product.price,
      quantity: 1
    }]
  })
})

onUnmounted(() => {
  document.documentElement.classList.remove('modal-open')
  document.body.classList.remove('modal-open')
  window.removeEventListener('keydown', closeWithEscape)
})

const closeWithEscape = (e) => {
  if (e.key === 'Escape') {
    closeModal()
  }
}
</script>

<style scoped>
@import '../assets/css/components/product-modal-horizontal.css';

.modal {
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10500;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10499;
}

.product-modal-container {
  position: relative;
}

.sku-row-modal {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 10px;
}

.sku-label-text {
  font-size: 8px;
  color: #bbb;
  font-weight: 500;
  letter-spacing: 1px;
}

.sku-value {
  background: transparent;
  color: #888888;
  font-family: 'Courier New', Courier, monospace;
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 0;
  margin: 4px 0;
}

.price-vat-label {
  font-size: 9px;
  color: #888;
  font-weight: 400;
  margin-top: 2px;
  letter-spacing: 0.5px;
}

/* Descrizione Prodotto Compattata */
.product-description-container {
  margin-top: 1rem !important;
  margin-bottom: 1rem !important;
}

.description-text {
  font-size: 13px;
  line-height: 1.5;
  color: #444;
  margin-bottom: 0.75rem !important;
  font-weight: 400;
}

.care-instructions {
  border-top: 1px solid #efefef;
  border-bottom: 1px solid #f0f0f0;
  padding-top: 10px;
  padding-bottom: 12px;
  margin-top: 0.9rem !important;
}

.care-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  color: #1a1a1a;
  cursor: pointer;
  text-align: left;
}

.care-toggle:hover {
  opacity: 0.8;
}

.care-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.1px;
  color: #111;
  font-weight: 700;
  display: inline-block;
}

.care-chevron {
  color: #111;
  transition: transform 0.25s ease;
  flex-shrink: 0;
}

.care-chevron.rotated {
  transform: rotate(180deg);
}

.size-guide-meta {
  margin: 0 0 10px;
  font-size: 11px;
  line-height: 1.4;
  color: #5a5a5a;
  letter-spacing: 0.2px;
}

.size-guide-model-fit {
  margin: 0 0 14px;
  font-size: 12px;
  line-height: 1.5;
  color: #4d4d4d;
  padding: 8px 10px;
  border-left: 1px solid #d9d9d9;
  background: #fafafa;
}

.trust-features-product {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
}

.trust-feature-item-product {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  color: inherit;
  text-decoration: none;
}

.feature-icon-product {
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #111;
  flex-shrink: 0;
}

.feature-text-product {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.main-text-product {
  font-size: 10px;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #111;
  font-weight: 700;
}

.sub-text-product {
  font-size: 10px;
  line-height: 1.2;
  color: #666;
  margin-top: 3px;
}

.care-list,
.detail-list {
  list-style: none;
  padding: 0;
  margin: 12px 0 0;
  display: grid;
  gap: 8px;
}

.care-list li,
.detail-list li {
  position: relative;
  padding-left: 14px;
  font-size: 12px;
  line-height: 1.5;
  color: #5a5a5a;
}

.care-list li::before,
.detail-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  top: 0;
  color: #111;
}

.btn-show-details {
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  color: #000;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  cursor: pointer;
  margin-top: 5px;
  transition: opacity 0.2s;
}

.btn-show-details-label {
  display: inline-block;
}

.btn-show-details:hover {
  opacity: 0.7;
}

.btn-show-details svg {
  transition: transform 0.3s ease;
}

.btn-show-details svg.rotated {
  transform: rotate(180deg);
}

.small-details {
  font-size: 12px !important;
  color: #777 !important;
  border-top: 1px solid #f0f0f0;
  padding-top: 10px;
}

/* Transizione Espansione */
.expand-enter-active, .expand-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 200px;
  opacity: 1;
  overflow: hidden;
}

.expand-enter-from, .expand-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0 !important;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
